import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  useListaCEPService,
  useProjetosService,
} from "../../../../../../../../use-cases/geo-veles";
import { useAppSelector } from "../../../../../../../hooks/store-hooks";
import { useEffect, useState } from "react";
import { CreateListaCEPForm, EditListaCEPForm } from "./forms";
import { PreviaFiltroLocalizacao } from "../previa-filtro-localizacao";
import { theme } from "../../../../../../../styles";
// Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { cepListLenghtToLabel } from "../../../../../../../../utils";

export const ListasCEP = (props: { purge: boolean; searchTerm: string }) => {
  // State
  const { listaCEP, projetos } = useAppSelector((state) => state);
  // Hook
  const { addListaCEPFilter, addPurgeListaCEPFilter } = useProjetosService();
  const {
    openCreateListaCEPForm,
    selectListaCEPToEdit,
    setupListaCEP,
    updateListaCEPStatus,
  } = useListaCEPService();

  useEffect(() => {
    setupListaCEP();
  }, [setupListaCEP]);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [listaCEPToDelete, setListaCEPToDelete] = useState<{
    id: number;
    nome: string;
  } | null>(null);

  const handleConfirmDelete = async () => {
    if (listaCEPToDelete) {
      try {
        await updateListaCEPStatus({
          idVelesGeoListaCEP: listaCEPToDelete.id,
          status: false, 
        });
      } catch (error) {
        console.error("Erro ao excluir a lista de CEP:", error);
      } finally {
        setConfirmDeleteOpen(false);
        setListaCEPToDelete(null);
      }
    }
  };

  return (
    <>
      <Box pl={3}>
        <form onSubmit={(e) => e.preventDefault()}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Button
                fullWidth
                onClick={(e) => {
                  e.preventDefault();
                  openCreateListaCEPForm(true);
                }}
                startIcon={<AddIcon />}
              >
                Criar nova lista de CEPs
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                onClick={() => {
                  const listasCEP = listaCEP?.listasCEP ?? [];
                  if (props.purge) {
                    listasCEP.forEach((cep) =>
                      addPurgeListaCEPFilter(cep.IdVelesGeoListaCEP)
                    );
                  } else {
                    listasCEP.forEach((cep) =>
                      addListaCEPFilter(cep.IdVelesGeoListaCEP)
                    );
                  }
                }}
              >
                Incluir todas as listas
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Grid container>
        <Grid item xs={6}>
          <List>
            {(listaCEP?.listasCEP ?? [])
              .filter((cepList) => {
                if (projetos?.filtrosSelecionados) {
                  if (
                    projetos.filtrosSelecionados.listasCEP?.includes(
                      cepList.IdVelesGeoListaCEP
                    )
                  ) {
                    return false;
                  }
                  if (
                    projetos.filtrosSelecionados.purgeListasCEP?.includes(
                      cepList.IdVelesGeoListaCEP
                    )
                  ) {
                    return false;
                  }
                }

                if (!cepList.Ativa) {
                  return false;
                }

                if (props.searchTerm === "") {
                  return true;
                }

                return cepList.Nome?.toUpperCase().includes(
                  props.searchTerm.toUpperCase()
                );
              })
              .map((cepList) => (
                <div key={cepList.IdVelesGeoListaCEP}>
                  <ListItem
                    secondaryAction={
                      <>
                        <Button
                          startIcon={<EditIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            selectListaCEPToEdit(cepList.IdVelesGeoListaCEP);
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          startIcon={<DeleteIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setListaCEPToDelete({
                              id: cepList.IdVelesGeoListaCEP,
                              nome: cepList.Nome || "essa lista",
                            });
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          Excluir
                        </Button>
                      </>
                    }
                    button
                    onClick={() => {
                      if (props.purge) {
                        addPurgeListaCEPFilter(cepList.IdVelesGeoListaCEP);
                      } else {
                        addListaCEPFilter(cepList.IdVelesGeoListaCEP);
                      }
                    }}
                  >
                    <ListItemText
                      primary={cepList.Nome?.toUpperCase()}
                      secondary={cepListLenghtToLabel(cepList.CEPs?.length)}
                    />
                  </ListItem>
                  <Divider color={theme.palette.primary.main} />
                </div>
              ))}
          </List>
        </Grid>

        {((projetos?.filtrosSelecionados?.localizacao?.length ?? 0) > 0 ||
          (projetos?.filtrosSelecionados?.purgeLocalizacao?.length ?? 0) > 0 ||
          (projetos?.filtrosSelecionados?.listasCEP?.length ?? 0) > 0 ||
          (projetos?.filtrosSelecionados?.purgeListasCEP?.length ?? 0) > 0) && (
            <Grid item xs={6}>
              <Box px={3}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="h5"
                      color="primary"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Localizações selecionadas
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PreviaFiltroLocalizacao />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>
          )}
      </Grid>

      <CreateListaCEPForm openForm={listaCEP.openCreateListaCEP} />
      <EditListaCEPForm openForm={listaCEP.openEditListaCEP} />

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setListaCEPToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a lista{" "}
            <strong>{listaCEPToDelete?.nome}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDeleteOpen(false);
              setListaCEPToDelete(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};